/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import {
  Alert, Dialog, DialogActions, DialogTitle, DialogContent, DialogContentText, Typography, Box,
} from '@mui/material';
import Button from '../components/button';
import { useApp } from '../hooks/use-app';
import RefreshButton from '../shared/refresh-button';
import CustomConfirmDialog from '../shared/material_components/custom-confirm-dialog';
import { checkForSignedInConnection } from './arcgis-connection-checker';
import arcGisConnection, { ArcGisConnectionObject } from './arcgis-connection';
import ArcGISErroredConnection from './arcgis-errored-connection-modal';

export const confirmLogoutText = "Are you sure you want to disconnect from ArcGIS? The ArcGIS layers linked to your account won't be available once you disconnect.";

interface ArcGISLoginButtonProps {
  allowRefresh: boolean;
  editing?: boolean;
  onChange?: () => void;
  forceCheckLoggedInStatus?: boolean;
}

const parseConnector = (connector) => {
  const parsedData = JSON.parse(connector.data);
  const { appId, portalUrl } = parsedData;
  return { appId, portalUrl };
};

const ArcGISLoginButton = ({
  allowRefresh, editing, onChange, forceCheckLoggedInStatus,
}: ArcGISLoginButtonProps) => {
  const app = useApp();
  const [logged, setLogged] = useState<boolean>(false);
  const [reloadedLayers, setReloadedLayers] = useState<boolean>(false);
  const [confirmLogoutOpened, setConfirmLogoutOpened] = useState<boolean>(false);
  const [connectionErrored, setConnectionErrored] = useState<boolean>(false);
  const [hasCheckedLoggedInStatus, setHasCheckedLoggedInStatus] = useState(forceCheckLoggedInStatus || false);
  const arcgisButtonCopy = logged ? 'DISCONNECT FROM ARCGIS' : 'CONNECT TO ARCGIS';
  const esriApiKey = app.globals.config.esri_api_key;
  const { connectors, esri_enterprise_connection_enabled } = app.globals.organization;

  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [recentConnection, setRecentConnection] = useState<ArcGisConnectionObject | undefined>(undefined);

  useEffect(() => {
    goFindOutIfTheyAreAlreadyLoggedIn();
  }, []);

  const handleConnect = async (connection) => {
    if (logged) {
      setConfirmLogoutOpened(true);
    } else {
      try {
        await connection.getAuthToken();
        await handleSignIn(connection);
      } catch (err) {
        // @ts-ignore
        const { message } = err;
        if (message !== 'ABORTED') {
          console.error('Error connecting to ArcGIS:', message);
          handleConnectionError();
        }
      }
    }
  };

  const goFindOutIfTheyAreAlreadyLoggedIn = async () => {
    const currentConnection = await checkForSignedInConnection(
      esriApiKey, connectors, !esri_enterprise_connection_enabled,
    );

    if (currentConnection) {
      handleSignIn(currentConnection);
    } else {
      handleSignedOut();
    }

    setHasCheckedLoggedInStatus(true);
  };

  const handleSignIn = async (connection) => {
    setRecentConnection(connection);
    setLogged(true);
    setOpenConfirmModal(false);
    await getLayerFromArcGIS(connection);
  };

  const handleSignedOut = () => {
    setLogged(false);
  };

  const handleConnectionError = () => {
    setConnectionErrored(true);
  };

  const acknowledgeConnectionError = () => {
    setConnectionErrored(false);
  };

  const handlePositive = () => {
    recentConnection?.destroyCredentials();
    setRecentConnection(undefined);
    window.location.reload();
  };

  const handleNegative = () => {
    setConfirmLogoutOpened(false);
  };

  const handleRefreshRequested = async () => {
    setReloadedLayers(true);
    await getLayerFromArcGIS(recentConnection);

    setReloadedLayers(false);
  };

  const getLayerFromArcGIS = async (connection) => {
    const layersFiltered = await connection?.fetchAllLayers();

    if (layersFiltered) {
      app.layerSettings.arcgisLayerList = layersFiltered;
    }
    app.layerSettings.refresh(); // this doesn't work

    onChange?.();
  };

  const handleOpenButtonClicked = () => {
    if (esri_enterprise_connection_enabled && connectors.length > 0 && !logged) {
      setOpenConfirmModal(true);
    } else if (recentConnection) {
      handleConnect(recentConnection);
    } else {
      handleConnect(arcGisConnection(esriApiKey));
    }
  };

  return (
    <>
      <Button
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'column',
          marginTop: '-3px',
          marginBottom: '-5px',
          marginLeft: '10px',
          alignSelf: 'flex-end',
        }}
        size="small"
        variant="outlined"
        color="secondary"
        onClick={handleOpenButtonClicked}
        disabled={!hasCheckedLoggedInStatus}
      >
        {arcgisButtonCopy}
      </Button>
      {logged && allowRefresh && (
        <RefreshButton
          loading={reloadedLayers}
          onClick={handleRefreshRequested}
        />
      )}
      <Dialog
        key={1}
        open={openConfirmModal}
        data-testid="arcgis-dialog"
        onClose={() => { setOpenConfirmModal(false); }}
        disableEnforceFocus
        PaperProps={{
          sx: {
            width: '700px',
            maxWidth: '700px',
            margin: 'auto',
            zIndex: '700 !important',
          },
        }}
        sx={{
          zIndex: 700,
        }}
        BackdropProps={{
          sx: {
            zIndex: 700,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, padding: '24px', paddingBottom: 0 }}>
          Connect to ArcGIS
        </DialogTitle>
        <Box m={2} p={1} pt={0}>
          <Typography>ArcGIS Enterprise connections are managed by your organization&apos;s admin.</Typography>
          <Typography>You can connect to your ArcGIS Online connections.</Typography>
        </Box>
        <DialogContent data-testid="arcgis-dialog">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{
              borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd', padding: '32px 0 32px', marginBottom: '32px',
            }}
          >
            <Typography data-testid="agol-button" sx={{ fontWeight: 'bold' }}>ArcGIS Online</Typography>
            <Button
              sx={{
                marginLeft: '10px',
              }}
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => handleConnect(arcGisConnection(esriApiKey))}
            >
              Connect
            </Button>
          </Box>
          { connectors.length > 0
            && (
              <>
                <Typography sx={{ fontWeight: 'bold', marginBottom: '16px' }}>ArcGIS Enterprise</Typography>
                <ArcGISErroredConnection isError={connectionErrored} acknowledge={acknowledgeConnectionError} />
              </>
            )}
          { connectors.map((connector) => {
            const { appId, portalUrl } = parseConnector(connector);
            return (
              <Box
                key={connector.resource_id}
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                mb={2}
                sx={{ borderBottom: '1px solid #ddd', paddingBottom: '14px' }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography sx={{ overflowWrap: 'anywhere' }}>{portalUrl}</Typography>
                  <Button
                    sx={{
                      marginLeft: '10px',
                    }}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleConnect(arcGisConnection(esriApiKey, appId, portalUrl))}
                  >
                    Connect
                  </Button>
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ padding: '0 16px 16px 16px' }}>
          <Button data-testid="close-button" color="neutral" onClick={() => { setOpenConfirmModal(false); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <CustomConfirmDialog
        open={confirmLogoutOpened}
        title="Disconnect from ArcGIS"
        positive="Yes, disconnect"
        onNegative={handleNegative}
        onPositive={handlePositive}
        positivetextcolor="secondary"
        maxWidth="sm"
      >
        {editing ? <Alert sx={{ marginTop: '5px', marginBottom: '16px' }} data-testid="record-editing-alert" severity="warning">The progress on the current record will be lost.</Alert> : null}
        <DialogContentText component="span" data-testid="dialog-content">
          <Typography variant="body1">
            {confirmLogoutText}
          </Typography>
        </DialogContentText>
      </CustomConfirmDialog>
    </>
  );
};

export default ArcGISLoginButton;
